"use client"

import clsx from "clsx"
import useEmblaCarousel from "embla-carousel-react"
import React, { useCallback, useEffect, useState, type JSX } from "react"

import ArrowCarousel from "../../../../../public/icons/arrow-up.svg"
import PageGrid from "../../PageGrid"

type ClimateActionCarouselProps = {
  slides: {
    id: number
    heading: string
    copy: string | JSX.Element
    image: JSX.Element
    logoActive: JSX.Element
    logoInactive: JSX.Element
  }[]
}

const ClimateActionCarousel: React.FC<ClimateActionCarouselProps> = (
  props: ClimateActionCarouselProps,
) => {
  const { slides } = props
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "center",
    skipSnaps: false,
    dragFree: false,
    duration: 20,
    slidesToScroll: 1,
  })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi],
  )

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const onSelect = useCallback((api: any) => {
    setSelectedIndex(api.selectedScrollSnap())
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on("reInit", onSelect)
    emblaApi.on("select", onSelect)
  }, [emblaApi, onSelect])

  return (
    <>
      {/* Embla viewport */}
      <div className="embla" ref={emblaRef}>
        <div className="embla__container">
          {slides.map((slide) => (
            <div
              key={slide.id}
              className="embla__slide flex-[0_0_100%] min-w-0"
            >
              <PageGrid>
                <div className="order-2 p-4 mb-8 lg:pl-4 lg:pr-0 lg:py-0 md:px-4 md:order-none lg:col-span-5 md:col-span-2 col-span-full md:mb-0">
                  <h2 className="heading3 md:heading2 lg:heading1 text-gray-2.5 xl:mb-12 mb-6">
                    {slide.heading}
                  </h2>
                  <p className="lg:body3 body4 text-gray-2.5">{slide.copy}</p>
                </div>
                <div className="order-1 mb-8 md:mb-0 md:order-none lg:col-span-7 md:col-span-2 col-span-full">
                  {slide.image}
                </div>
              </PageGrid>
            </div>
          ))}
        </div>
      </div>

      {/* Desktop logo navigation */}
      <PageGrid isGrid={false} className="hidden md:mt-16 md:flex">
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            onClick={() => scrollTo(index)}
            className={clsx(
              "w-1/4 flex items-center justify-center md:col-span-1 lg:col-span-3 lg:pb-6 md:pb-[16px] md:h-[82px] lg:h-[90px] border-l-0 border-r-0 border-t-0 border-solid border-b-2",
              selectedIndex === index ? "border-b-blue-50" : "border-b-gray-10",
            )}
            type="button"
            aria-label={`Go to slide ${index + 1}`}
          >
            {selectedIndex === index ? slide.logoActive : slide.logoInactive}
          </button>
        ))}
      </PageGrid>

      {/* Mobile navigation */}
      <PageGrid
        isGrid={false}
        className="flex justify-between items-center w-full !mx-0 md:hidden"
      >
        <button
          onClick={scrollPrev}
          disabled={!canScrollPrev}
          className="p-4 disabled:opacity-50 disabled:cursor-not-allowed"
          type="button"
          aria-label="Previous slide"
        >
          <ArrowCarousel className="w-4 h-4 transform -rotate-90 text-gray-2.5" />
        </button>
        {slides[selectedIndex].logoActive}
        <button
          onClick={scrollNext}
          disabled={!canScrollNext}
          className="p-4 disabled:opacity-50 disabled:cursor-not-allowed"
          type="button"
          aria-label="Next slide"
        >
          <ArrowCarousel className="w-4 h-4 rotate-90 text-gray-2.5" />
        </button>
      </PageGrid>
    </>
  )
}

export default ClimateActionCarousel
